import { mergeTranslations, type I18nProvider } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";
import { spanishCrmMessages } from "./spanishCrmMessages";
import { spanishRaMessages } from "./spanishRaMessages";
import {
  hubEnglishMessages,
  hubSpanishMessages,
} from "@/components/fretum/i18n";
import {
  marketEnglishMessages,
  marketSpanishMessages,
} from "@/components/market-intel/i18n";
import {
  competitionEnglishMessages,
  competitionSpanishMessages,
} from "@/components/competitors/i18n";
import {
  marketingEnglishMessages,
  marketingSpanishMessages,
} from "@/components/marketing/i18n";

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
    oauth: {
      authorize: "Authorize Application",
      authorize_details: "This application wants to access your account.",
      access_scope:
        "It will be able to read and modify your data, with the same permissions as your account.",
      approved: "Authorization Approved",
      close_tab: "You can now close this tab.",
      no_request: "No authorization request found",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
    oauth: {
      authorize: "Autoriser l'application",
      authorize_details: "Cette application souhaite accéder à votre compte.",
      access_scope:
        "Elle pourra lire et modifier vos données, avec les mêmes droits que votre compte.",
      approved: "Autorisation accordée",
      close_tab: "Vous pouvez fermer cet onglet.",
      no_request: "Aucune demande d'autorisation trouvée",
    },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
  hubEnglishMessages,
  marketEnglishMessages,
  competitionEnglishMessages,
  marketingEnglishMessages,
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
);

const spanishCatalog = mergeTranslations(
  englishCatalog,
  spanishRaMessages,
  spanishCrmMessages,
  hubSpanishMessages,
  marketSpanishMessages,
  competitionSpanishMessages,
  marketingSpanishMessages,
);

/**
 * Avante is a Spanish CRM: the UI starts in Spanish whatever the browser
 * language. A language the user picks in the settings is persisted by
 * react-admin's store (`locale` key) and restored on the next visit; this is
 * only the default when nothing was picked.
 */
export const getInitialLocale = (): "es" | "en" | "fr" => "es";

/**
 * `<html lang>` follows the app locale: screen readers, hyphenation and the
 * native date/time pickers of some browsers read it.
 */
export const syncDocumentLang = (locale: string) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
};

const polyglotProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "fr") {
      return frenchCatalog;
    }
    if (locale === "es") {
      return spanishCatalog;
    }
    return englishCatalog;
  },
  getInitialLocale(),
  [
    { locale: "es", name: "Español" },
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
  ],
  { allowMissing: true },
);

export const i18nProvider: I18nProvider = {
  ...polyglotProvider,
  changeLocale: async (locale, options) => {
    await polyglotProvider.changeLocale(locale, options);
    syncDocumentLang(locale);
  },
};

syncDocumentLang(i18nProvider.getLocale());

export const testI18nProvider = polyglotI18nProvider(
  () => englishCatalog,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
